/**
 * <OtpForm> — the second step of tenant login (CC-03 OTP branch).
 *
 * Rendered after LoginForm's `onOtpRequired` fires. Same route (/login),
 * different card content. The apps' login page owns the step-state;
 * <OtpForm> is purely the OTP entry view.
 *
 * Per UI-SPEC §4:
 *   - Single 6-digit `<Input>` with `inputMode="numeric"` `pattern="[0-9]*"`
 *     `autoComplete="one-time-code"`, `font-mono tabular-numerals`, `dir="ltr"`.
 *   - ICU `{ttl, number}` sub-copy + `{maskedEmail}` interpolation.
 *   - Back button clears local state and calls `onBack()`.
 *   - Focus moves to the OTP input on mount.
 *   - Error branching: OTP_INVALID → invalid key; OTP_EXPIRED → expired key.
 *
 * D-11 security boundary: the access token is minted by the BACKEND in
 * `completeOtp` — the login step NEVER issues one pre-OTP. This file does
 * not hold any token state.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { match, P } from "ts-pattern";
import { Alert, Button, Form, FormField, Input } from "@linkhr/design-system";
import { otpSchema, type OtpSchema } from "../schemas/authSchemas.js";
import type { AuthSnapshot, Audience } from "../types.js";
import { useAuth } from "../react/useAuth.js";
import { performOtpResend } from "../actions/otp.js";
import { ADMIN_AUDIENCE_ERROR_CODE, AdminAudienceNotice } from "./AdminAudienceNotice.js";
import { accountingRefusalKey } from "../errors/authErrorCodes.js";

export interface OtpFormProps {
  otpSessionToken: string;
  ttl: number;
  channel: "email" | "sms";
  maskedEmail: string;
  apiBaseUrl: string;
  onSuccess: (snapshot: AuthSnapshot) => void;
  onBack: () => void;
  /**
   * Suppress the form's internal `<h1>` + sub-copy when the surrounding page
   * (Phase 05.2 Plan 09 LoginPage) renders the canonical title/subtitle in
   * the card chrome.
   */
  hideHeading?: boolean;
  /**
   * Id of the heading the form's `aria-labelledby` points at. When the page
   * owns the heading (`hideHeading`), pass the page's heading id so the form
   * keeps an accessible name (the internal `otp-heading` is not rendered).
   * @default "otp-heading"
   */
  headingId?: string;
  /**
   * Audience to verify under. Omit — as all three per-audience apps do — and
   * the app's own audience is used. The global login passes the audience the
   * credentials were accepted for, which it only learns at login time.
   */
  audience?: Audience;
}

export function OtpForm({
  otpSessionToken,
  ttl,
  maskedEmail,
  apiBaseUrl,
  onSuccess,
  onBack,
  hideHeading = false,
  headingId = "otp-heading",
  audience,
}: OtpFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const [serverErrorKey, setServerErrorKey] = useState<string | null>(null);
  // The wait a rate-limited code check names (the `seconds` of the rate-limit sentence).
  const [waitSeconds, setWaitSeconds] = useState(0);
  // be-788 / CC-431: AUTH_ADMIN_AUDIENCE_NOT_PERMITTED renders the SERVER's
  // pre-localized title + an employee-app link, never a generic error.
  const [audienceError, setAudienceError] = useState<string | null>(null);
  // Track the live token locally so a resend can swap it without parent
  // round-trip. The prop is the *initial* value only.
  const [liveSessionToken, setLiveSessionToken] = useState(otpSessionToken);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  const methods = useForm<OtpSchema>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  const codeRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  const onSubmit = methods.handleSubmit(async (values) => {
    setServerErrorKey(null);
    setAudienceError(null);
    setResendNotice(null);
    try {
      const result = await auth.completeOtp({
        otpSessionToken: liveSessionToken,
        code: values.code,
        apiBaseUrl,
        ...(audience !== undefined ? { audience } : {}),
      });
      onSuccess(result.snapshot);
    } catch (err) {
      const apiErr =
        err !== null && typeof err === "object" ? (err as Record<string, unknown>) : {};
      const errorCode = String((apiErr.errorCode as string | undefined) ?? "");
      // ApiError sets message = the RFC 7807 title — the server's pre-localized
      // copy at this seam. Branch the audience 403 BEFORE the generic mapping.
      if (errorCode === ADMIN_AUDIENCE_ERROR_CODE) {
        const serverTitle = typeof apiErr.title === "string" ? apiErr.title.trim() : "";
        setAudienceError(serverTitle !== "" ? serverTitle : t("session.admin_access_revoked"));
        return;
      }
      // Too many wrong codes for this person: say so, with the wait, never "invalid code".
      if (errorCode === "AUTH_RATE_LIMITED" || apiErr.status === 429) {
        const seconds = typeof apiErr.retryAfterSeconds === "number" ? apiErr.retryAfterSeconds : 0;
        setWaitSeconds(seconds);
        setServerErrorKey(
          seconds > 0 ? "login.error.rate_limited_retry" : "login.error.rate_limited",
        );
        return;
      }
      // useTranslation("auth") scopes keys — no "auth." prefix here.
      // The server sends the AUTH_-prefixed codes; the bare ones are this client's bodyless fallbacks.
      const key = match(errorCode)
        .with(P.union("AUTH_OTP_EXPIRED", "OTP_EXPIRED"), () => "otp.error.expired")
        .with(P.union("AUTH_OTP_INVALID", "OTP_INVALID"), () => "otp.error.invalid")
        .with("AUTH_OTP_TOO_MANY_ATTEMPTS", () => "otp.error.too_many")
        .otherwise(() => accountingRefusalKey(errorCode) ?? "error.network");
      setServerErrorKey(key);
    }
  });

  const isSubmitting = methods.formState.isSubmitting;

  const handleResend = async (): Promise<void> => {
    setServerErrorKey(null);
    setResendNotice(null);
    setResending(true);
    try {
      const result = await performOtpResend({
        otpSessionToken: liveSessionToken,
        apiBaseUrl,
      });
      setLiveSessionToken(result.otpSessionToken);
      setResendNotice("otp.resend.success");
      methods.setValue("code", "");
      codeRef.current?.focus();
    } catch (err) {
      const errorCode =
        err !== null && typeof err === "object" && "errorCode" in err
          ? String((err as { errorCode?: string }).errorCode ?? "")
          : "";
      // A 429 is the rate limit even when a gateway sends its own code in the body.
      const limited =
        err !== null && typeof err === "object" && (err as { status?: number }).status === 429;
      const key = match(limited ? "AUTH_RATE_LIMITED" : errorCode)
        .with("AUTH_RATE_LIMITED", () => "otp.resend.error.rate_limited")
        .with(P.union("AUTH_OTP_EXPIRED", "OTP_EXPIRED"), () => "otp.error.expired")
        .otherwise(() => "error.network");
      setServerErrorKey(key);
    } finally {
      setResending(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit} aria-labelledby={headingId}>
        {!hideHeading && (
          <>
            <h1 id={headingId} className="text-heading text-default">
              {t("otp.heading")}
            </h1>
            <p className="text-body text-muted">{t("otp.subtext", { ttl, maskedEmail })}</p>
          </>
        )}

        {serverErrorKey !== null && (
          <Alert variant="danger" description={t(serverErrorKey, { seconds: waitSeconds })} />
        )}
        {audienceError !== null && <AdminAudienceNotice serverTitle={audienceError} />}
        {resendNotice !== null && <Alert variant="success" description={t(resendNotice)} />}

        <FormField<OtpSchema, "code">
          name="code"
          label={t("otp.code_label")}
          required
          render={(field) => (
            <Input
              id={field.id}
              name={field.name}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={6}
              bidi="ltr"
              className="font-mono tabular-nums text-center tracking-[0.5em]"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={(node) => {
                field.ref(node);
                codeRef.current = node;
              }}
              invalid={field.invalid}
              aria-describedby={field["aria-describedby"]}
              aria-invalid={field["aria-invalid"]}
              aria-required={field["aria-required"]}
            />
          )}
        />

        <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
          {t("otp.cta")}
        </Button>
        <div className="flex items-center justify-between gap-sm">
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            {t("otp.back")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={resending || isSubmitting}
            onClick={() => {
              void handleResend();
            }}
          >
            {resending ? t("otp.resend.sending") : t("otp.resend.cta")}
          </Button>
        </div>
      </Form>
    </FormProvider>
  );
}
